import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useLocale, useT } from '../../contexts/LocaleProvider';
import { deptLabel } from '../../lib/departments';
import { buildDisplayNameFor } from '../../lib/eventNames';

const isoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const dayIsoOf = (year, monthIdx, day) =>
  isoDate(new Date(year, monthIdx, day));

const overlaps = (event, iso) => event.start_date <= iso && iso <= event.end_date;

// Half-days count as 0.5 of a person off.
function loadFor(event) {
  return event.half_day === 'am' || event.half_day === 'pm' ? 0.5 : 1;
}

// Pick a tone given an absent/total ratio. Returns inline styles using HSL
// via the design tokens so both light + dark mode look right.
function pressureTone(ratio) {
  if (ratio <= 0)       return { bg: 'transparent',              fg: 'hsl(var(--text-muted))', strong: false };
  if (ratio < 0.25)     return { bg: 'hsl(var(--success-soft))', fg: 'hsl(var(--success))',    strong: false };
  if (ratio < 0.50)     return { bg: 'hsl(var(--warning-soft))', fg: 'hsl(var(--warning))',    strong: false };
  if (ratio < 0.75)     return { bg: 'hsl(30 75% 78%)',          fg: 'hsl(30 80% 25%)',        strong: true };
  return                       { bg: 'hsl(var(--danger-soft))',  fg: 'hsl(var(--danger))',     strong: true };
}

function fmtCount(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, '');
}

export default function PressureGrid({ year, monthIdx, events, employees }) {
  const t = useT();
  const { locale, intlLocale } = useLocale();
  const todayIso = isoDate(new Date());
  const [selected, setSelected] = useState(null);  // { dept, day } | null

  // How many people each department has · the denominator for ratios.
  const SANS = t('directory.noDept');
  const totalByDept = useMemo(() => {
    const m = new Map();
    for (const e of employees) {
      const d = (e.departement || '').trim() || SANS;
      m.set(d, (m.get(d) || 0) + 1);
    }
    return m;
  }, [employees, SANS]);

  // Resolve "effective department" of an absence: explicit on the event
  // first, else fall back to the employee's current dept.
  const empDeptByEmail = useMemo(() => {
    const m = new Map();
    for (const e of employees) {
      if (e.email) m.set(e.email.toLowerCase(), (e.departement || '').trim());
    }
    return m;
  }, [employees]);

  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const days = useMemo(
    () => Array.from({ length: daysInMonth }, (_, i) => i + 1),
    [daysInMonth],
  );

  // Group absences by (dept, day) so we can both render the load AND, on
  // click, surface who's off.
  const eventsByCell = useMemo(() => {
    const out = new Map();  // Map<dept, Map<day, Event[]>>
    const absences = (events || []).filter(e => e.kind === 'absence');
    for (const e of absences) {
      const explicit = (e.departement || '').trim();
      const dept = explicit || empDeptByEmail.get((e.employee_email || '').toLowerCase()) || '';
      if (!dept) continue;
      if (!out.has(dept)) out.set(dept, new Map());
      const perDay = out.get(dept);
      for (const day of days) {
        const iso = dayIsoOf(year, monthIdx, day);
        if (overlaps(e, iso)) {
          if (!perDay.has(day)) perDay.set(day, []);
          perDay.get(day).push(e);
        }
      }
    }
    return out;
  }, [events, empDeptByEmail, days, year, monthIdx]);

  const visibleDepts = useMemo(() => {
    const set = new Set(totalByDept.keys());
    for (const d of eventsByCell.keys()) set.add(d);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [totalByDept, eventsByCell]);

  // First-name disambiguation across the visible event set ("Geoffrey M.")
  const displayNameFor = useMemo(
    () => buildDisplayNameFor(events || []),
    [events],
  );

  const onCellClick = (dept, day, hasEvents) => {
    if (!hasEvents) return;
    setSelected((cur) =>
      cur && cur.dept === dept && cur.day === day ? null : { dept, day },
    );
  };

  // Resolved selection · the actual event list for the panel below the grid.
  const selectedDetail = useMemo(() => {
    if (!selected) return null;
    const list = eventsByCell.get(selected.dept)?.get(selected.day) || [];
    if (list.length === 0) return null;
    const iso = dayIsoOf(year, monthIdx, selected.day);
    return { dept: selected.dept, day: selected.day, iso, list };
  }, [selected, eventsByCell, year, monthIdx]);

  if (visibleDepts.length === 0) {
    return (
      <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-10 text-center text-sm text-[hsl(var(--text-soft))]">
        {t('common.empty')}
      </div>
    );
  }

  const gridTemplate = `220px repeat(${daysInMonth}, minmax(28px, 1fr))`;
  const halfTag = (e) => e.half_day === 'am' ? ` · ${t('calendar.halfDay.am')}`
                       : e.half_day === 'pm' ? ` · ${t('calendar.halfDay.pm')}`
                       : '';

  return (
    <div className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <div className="grid text-[12px] min-w-max" style={{ gridTemplateColumns: gridTemplate }}>
          {/* Header row · day numbers */}
          <div className="bg-[hsl(var(--bg-subtle))] border-b border-border px-3 py-2 font-medium text-[hsl(var(--text-muted))]">
            {t('calendar.pressure.dept')}
          </div>
          {days.map(d => {
            const iso = dayIsoOf(year, monthIdx, d);
            const isToday = iso === todayIso;
            return (
              <div key={d} className="bg-[hsl(var(--bg-subtle))] border-b border-l border-[hsl(var(--rule))] px-1 py-2 text-center font-num">
                <span className={isToday
                  ? 'inline-flex items-center justify-center h-5 w-5 rounded-full bg-[hsl(var(--accent))] text-white font-semibold'
                  : 'text-[hsl(var(--text-muted))]'}>
                  {d}
                </span>
              </div>
            );
          })}

          {/* Body · one row per department */}
          {visibleDepts.map((dept, rowIdx) => {
            const total = totalByDept.get(dept) || 0;
            const perDay = eventsByCell.get(dept) || new Map();
            const rowBorder = rowIdx === 0 ? '' : 'border-t border-[hsl(var(--rule))]';
            return (
              <React.Fragment key={dept}>
                <div className={`px-3 py-2 ${rowBorder} flex items-center gap-2 min-w-0`}>
                  <span className="truncate">{deptLabel(dept, locale)}</span>
                  {total > 0 && (
                    <span className="text-[11px] text-[hsl(var(--text-muted))] font-num shrink-0">({total})</span>
                  )}
                </div>
                {days.map(d => {
                  const cellEvents = perDay.get(d) || [];
                  const count = cellEvents.reduce((acc, e) => acc + loadFor(e), 0);
                  const ratio = total > 0 ? count / total : (count > 0 ? 1 : 0);
                  const tone = pressureTone(ratio);
                  const isSelected = selected && selected.dept === dept && selected.day === d;
                  const clickable = cellEvents.length > 0;
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => onCellClick(dept, d, clickable)}
                      disabled={!clickable}
                      className={`text-center py-1.5 font-num border-l border-[hsl(var(--rule))] ${rowBorder} transition ${
                        clickable
                          ? 'cursor-pointer hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent)/.4)] focus:ring-inset'
                          : 'cursor-default'
                      } ${isSelected ? 'ring-2 ring-inset ring-[hsl(var(--text))]' : ''}`}
                      style={{ background: tone.bg, color: tone.fg, fontWeight: tone.strong ? 600 : 400 }}
                      title={clickable ? `${fmtCount(count)} / ${total} · ${Math.round(ratio * 100)}%` : ''}
                    >
                      {clickable ? fmtCount(count) : <span className="opacity-30">·</span>}
                    </button>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="px-4 py-2.5 border-t border-border flex items-center gap-3 text-[11.5px] text-[hsl(var(--text-muted))]">
        <span>{t('calendar.pressure.legend')}</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-5 rounded-sm" style={{ background: 'hsl(var(--success-soft))' }} />
          &lt; 25%
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-5 rounded-sm" style={{ background: 'hsl(var(--warning-soft))' }} />
          25–50%
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-5 rounded-sm" style={{ background: 'hsl(30 75% 78%)' }} />
          50–75%
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-5 rounded-sm" style={{ background: 'hsl(var(--danger-soft))' }} />
          ≥ 75%
        </span>
      </div>

      {/* Details for the selected cell */}
      {selectedDetail && (
        <div className="border-t border-border bg-[hsl(var(--bg-subtle))]">
          <div className="px-4 py-3 flex items-center justify-between border-b border-[hsl(var(--rule))]">
            <div className="text-[13px]">
              <span className="font-semibold">{deptLabel(selectedDetail.dept, locale)}</span>
              <span className="text-[hsl(var(--text-muted))]">
                {' · '}
                {new Intl.DateTimeFormat(intlLocale, { weekday: 'long', day: '2-digit', month: 'long' })
                  .format(new Date(selectedDetail.iso))}
              </span>
            </div>
            <button type="button" onClick={() => setSelected(null)}
              className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]" aria-label={t('common.close') || 'Close'}>
              <X className="h-4 w-4" />
            </button>
          </div>
          <ul className="divide-y divide-[hsl(var(--rule))]">
            {selectedDetail.list.map(e => (
              <li key={e.id} className="px-4 py-2 text-[13px] flex items-center justify-between gap-3">
                <span className="truncate">
                  <span className="font-medium">{displayNameFor(e.employee_full_name) || e.employee_full_name}</span>
                  <span className="text-[hsl(var(--text-muted))]">
                    {' · '}{e.title}{halfTag(e)}
                  </span>
                </span>
                <span className="text-[11px] text-[hsl(var(--text-muted))] font-num shrink-0">
                  {e.start_date === e.end_date
                    ? new Intl.DateTimeFormat(intlLocale, { day: '2-digit', month: 'short' }).format(new Date(e.start_date))
                    : `${new Intl.DateTimeFormat(intlLocale, { day: '2-digit', month: 'short' }).format(new Date(e.start_date))} → ${new Intl.DateTimeFormat(intlLocale, { day: '2-digit', month: 'short' }).format(new Date(e.end_date))}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
